import './header.scss';

import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { ClientOtpModal } from '@/components/Client/AutoCredit/shared/entities';
import type { ModalOpenerType } from '@/components/Client/AutoCredit/shared/entities/client-otp-modal';
import { getLeasingCompanyFromParams } from '@/components/Client/api/client-create.api';
import { ClientContext } from '@/components/Client/client.context';
import { setClient } from '@/components/Client/client.store';
import { LanguageChanger } from '@/components/Client/shared/entities';
import { closeWebView } from '@/components/Client/shared/webview/base-actions';
import {
  CLIENT_ROUTES,
  getFullClientRoute
} from '@/components/Shared/routes/routes';
import {
  CONSENT_TO_THE_PROCESSING_OF_PERSONAL_DATA_LINK,
  PRIVACY_POLICY_LINK
} from '@/constants';
import useAppNavigate from '@/hooks/useAppNavigation';
import AllurFinanceLogo from '@/media/allur-finance.svg?react';
import { CloseOutlined, DownOutlined } from '@/media/antd-icons';
import MarkPoint from '@/media/icons/mark-point.svg?react';
import UserRounded from '@/media/icons/user-rounded.svg?react';
import { getUserInitials } from '@/services/helpers/strings';
import { getClientToken, removeClientToken } from '@/services/user-hash';
import { useAppDispatch, useAppSelector } from '@/store';

import { CityModal } from '../../entities';
import { UserDropdown } from './entities';
import useAuth from './hooks/useAuth';

const Header: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { navigate } = useAppNavigate();
  const dispatch = useAppDispatch();
  const { city, clientAdditionalInfo, isInWebView } = useAppSelector(
    (state) => state.client
  );
  const { autoCreditRequest } = useAppSelector(
    (state) => state.clientAutoCredit
  );
  const { filteredQrBrand, filteredQrDealer, filteredQrCity } =
    useContext(ClientContext);

  const [cityModalOpened, setCityModalOpened] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState<ModalOpenerType>('');

  const { handleCreateOtp, handleValidateOtp, handleResendOtp } = useAuth({
    setShowOtpModal
  });

  const showHeaderControllers = autoCreditRequest?.sendTo !== 'leasing';
  const canOpenModal = location.pathname.includes(
    getFullClientRoute(CLIENT_ROUTES.autoCreditCreate)
  );

  let logoLink = `/${i18n.language}${getFullClientRoute(
    CLIENT_ROUTES.autoCreditCreate
  )}`;
  const leasingCompany = getLeasingCompanyFromParams();
  if (leasingCompany) {
    const currentParams = new URLSearchParams(window.location.search);
    const params = new URLSearchParams();
    params.set('leasing', leasingCompany);
    params.set('city', currentParams.get('city') ?? '');
    logoLink += `?${params.toString()}`;
  }

  const handleNavigateToRequests = () => {
    const token = getClientToken();
    if (token?.accessToken) {
      navigate(getFullClientRoute(CLIENT_ROUTES.autoCredit) + '?tab=active');
    }
  };

  const handleLogout = () => {
    const token = getClientToken();
    removeClientToken();
    dispatch(setClient(null));
    if (token?.accessToken) {
      window.location.reload();
    }
  };

  const handleLogin = () => {
    const token = getClientToken();
    if (!token?.accessToken && canOpenModal) {
      handleLogout();
      setShowOtpModal('client-info-form');
    }
  };

  if (isInWebView) {
    const isLeasingCompany =
      !!getLeasingCompanyFromParams() ||
      autoCreditRequest?.sendTo === 'leasing';

    return (
      <header className="client-header">
        <div className="client-header__container">
          <h1 className="client-header__title">
            {isLeasingCompany
              ? t('client.leasing')
              : t('client.auto-credit-title')}
          </h1>

          <div className="client-header__controllers">
            {city && (
              <button
                disabled={!!filteredQrCity}
                onClick={() => setCityModalOpened(true)}
                className="client-header__button client-header__button--wide"
              >
                <span className="client-header__button-text">
                  {t(`client.${city.name}`, { defaultValue: city.name })}
                </span>
              </button>
            )}
            <button
              onClick={() => closeWebView()}
              className="client-header__close-button"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        <CityModal isOpened={cityModalOpened} onClose={setCityModalOpened} />
      </header>
    );
  }

  return (
    <header className="client-header">
      <div className="client-header__container">
        {filteredQrBrand || filteredQrDealer ? (
          <AllurFinanceLogo className="client-header__logo" />
        ) : (
          <Link
            id="header-logo"
            to={logoLink}
            className="client-header__logo-box"
          >
            <AllurFinanceLogo className="client-header__logo" />
          </Link>
        )}

        {showHeaderControllers && (
          <div className="client-header__controllers">
            {city && (
              <button
                disabled={!!filteredQrCity}
                onClick={() => setCityModalOpened(true)}
                className="client-header__button client-header__button--wide"
              >
                <MarkPoint className="client-header__button-icon" />
                <span className="client-header__button-text">
                  {t(`client.${city.name}`, { defaultValue: city.name })}
                </span>
              </button>
            )}
            <LanguageChanger />
            {clientAdditionalInfo.initialValues ? (
              <UserDropdown
                items={[
                  {
                    key: 'applications',
                    label: t('client.my-requests'),
                    onClick: handleNavigateToRequests
                  },
                  {
                    key: 'logout',
                    label: t('client.logout'),
                    onClick: handleLogout
                  }
                ]}
              >
                <button className="client-header__button client-header__button--user">
                  <span className="client-header__username">
                    {getUserInitials(clientAdditionalInfo.initialValues) ||
                      t('client.profile')}
                  </span>
                  <DownOutlined className="client-header__button-icon" />
                </button>
              </UserDropdown>
            ) : (
              <button
                onClick={handleLogin}
                className="client-header__button client-header__button--user"
              >
                <span className="client-header__username">
                  {t('client.login')}
                </span>
                <UserRounded className="client-header__button-icon" />
              </button>
            )}
          </div>
        )}
      </div>

      <CityModal isOpened={cityModalOpened} onClose={setCityModalOpened} />

      <ClientOtpModal
        title={t('client.personal-check')}
        onClientSubmit={handleCreateOtp}
        onOtpSubmit={handleValidateOtp}
        onSMSRetry={handleResendOtp}
        openedModal={showOtpModal}
        setOpenedModal={setShowOtpModal}
        onClose={() => setShowOtpModal('')}
        mainAgreements={{
          policyLink: PRIVACY_POLICY_LINK,
          agreementLink: CONSENT_TO_THE_PROCESSING_OF_PERSONAL_DATA_LINK
        }}
      />
    </header>
  );
};

export default Header;
